import {
  Text,
  Flex,
  Button,
  Box,
  Tag,
  TagLabel,
  Tooltip,
  Checkbox,
} from '@chakra-ui/react'
import {CheckIcon} from '@chakra-ui/icons'
import {Trans, useTranslation} from 'react-i18next'
import PropTypes from 'prop-types'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import {customerType} from '../../../../sharedPropTypes'
import {StarRating} from '../../../StarRating'
import {toCurrency} from '../../../../../utils/formatter'
import {MarkUnreadMailboxIcon} from '../../../../icons/MarkUnreadMailboxIcon'

dayjs.extend(relativeTime)

const SelectAllCheckbox = ({table: {options: {meta = {}} = {}} = {}}) => {
  const {t} = useTranslation('invitePayers')
  const {
    selectedCustomers = [],
    customers = [],
    onSelectAll,
    invitationType,
  } = meta

  const allSelected =
    customers.length > 0 && selectedCustomers.length === customers.length
  const someSelected =
    selectedCustomers.length > 0 && selectedCustomers.length < customers.length
  const isDisabled = invitationType === 'individual'
  const isChecked = isDisabled ? false : allSelected
  const isIndeterminate = isDisabled ? false : someSelected
  const onChange = (event) => onSelectAll?.(event.target.checked)

  return (
    <Checkbox
      className="fs-unmask"
      isDisabled={isDisabled}
      isChecked={isChecked}
      isIndeterminate={isIndeterminate}
      onChange={onChange}
      size="md"
      sx={
        !isDisabled
          ? {
              '& .chakra-checkbox__control': {
                _hover: {
                  borderColor: 'black !important',
                },
                _indeterminate: {
                  bg: 'black',
                  borderColor: 'black !important',
                },
                _checked: {
                  bg: 'black',
                  borderColor: 'black !important',
                  _hover: {
                    bg: 'black',
                    borderColor: 'black !important',
                  },
                },
              },
            }
          : undefined
      }
    >
      <Text fontSize="xs" fontWeight="medium" textTransform="uppercase">
        {t('modal.steps.selectPayers.buttons.selectAll')}
      </Text>
    </Checkbox>
  )
}

SelectAllCheckbox.propTypes = {
  table: PropTypes.shape({
    options: PropTypes.shape({
      meta: PropTypes.shape({
        selectedCustomers: PropTypes.arrayOf(customerType),
        customers: PropTypes.arrayOf(customerType),
        onSelectAll: PropTypes.func,
        invitationType: PropTypes.string,
      }),
    }),
  }).isRequired,
}

const previouslyNotifiedAt = (previouslySelectedAt) => {
  return dayjs(previouslySelectedAt).fromNow()
}

const CustomerNameCell = ({getValue, row}) => {
  const {t} = useTranslation('invitePayers')
  const customer = row.original
  const hasUnpaidInvoices = customer.averageInvoiceSize.cents > 0

  return (
    <Flex flexDirection="column" gap={2}>
      <Text fontWeight="regular" mb={1}>
        {getValue()}
      </Text>
      {customer.description && (
        <Text fontSize="xs" color="gray.600">
          {customer.description}
        </Text>
      )}
      {hasUnpaidInvoices && (
        <Tooltip
          label={t('modal.steps.selectPayers.customerHasUnpaidInvoicesTooltip')}
          placement="bottom"
        >
          <Tag variant="status" colorScheme="info" width="fit-content">
            <MarkUnreadMailboxIcon
              mr={2}
              boxSize="12px"
              color="mld.success.700"
            />
            <TagLabel textTransform="uppercase">
              {t('modal.steps.selectPayers.customerHasUnpaidInvoicesLabel')}
            </TagLabel>
          </Tag>
        </Tooltip>
      )}
    </Flex>
  )
}

CustomerNameCell.propTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      onToggleSelect: PropTypes.func.isRequired,
      description: PropTypes.string,
      averageInvoiceSize: PropTypes.shape({
        cents: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}

const invoiceSizeCell = ({getValue}) => {
  const money = getValue()
  return (
    <Text fontWeight="regular" align="right" pr={10}>
      {toCurrency(money.cents, '$0,0')}
    </Text>
  )
}

// The rating blurb is written per star rating, so it is keyed off the score.
// The decision's own reason key is not usable here: the risk engine emits two
// families of them (a descriptive one such as new_customer_limited_data as well
// as five_star and friends) and only the latter has copy on this screen.
const RATING_KEYS = [
  'zero_star',
  'one_star',
  'two_star',
  'three_star',
  'four_star',
  'five_star',
]

const CustomerRatingCell = ({getValue}) => {
  const value = getValue()
  const {t} = useTranslation('invitePayers')
  const ratingKey = RATING_KEYS[value.score]

  return (
    <Flex direction="column" textAlign="left" gap="xxs">
      <Flex
        backgroundColor="mld.neutral.100"
        borderRadius="md"
        p="xxs"
        width="fit-content"
      >
        <StarRating
          score={value.score}
          size="16px"
          colorActive="orange.400"
          colorInactive="gray.300"
        />
      </Flex>
      {ratingKey && (
        <Text fontSize="xs" color="mld.neutral.700" mt={1}>
          <Trans
            t={t}
            i18nKey={`modal.steps.selectPayers.customerRatings.${ratingKey}`}
            components={{bold: <b />}}
          />
        </Text>
      )}
    </Flex>
  )
}

CustomerRatingCell.propTypes = {
  getValue: PropTypes.func.isRequired,
}

const PricingBandCell = ({getValue}) => {
  const {t} = useTranslation('invitePayers')
  const value = getValue()

  if (
    value.feeRangeMin === 'unavailable' ||
    value.feeRangeMax === 'unavailable'
  ) {
    return (
      <Text fontWeight="regular">
        {t('modal.steps.selectPayers.unavailable')}
      </Text>
    )
  }

  return (
    <Text fontWeight="regular">
      {`${value.feeRangeMin.toFixed(2)} - ${value.feeRangeMax.toFixed(2)}%`}
    </Text>
  )
}

PricingBandCell.propTypes = {
  getValue: PropTypes.func.isRequired,
}

const InviteButtonCell = ({row, table: {options: {meta = {}} = {}} = {}}) => {
  const customer = row.original
  const {t} = useTranslation('invitePayers')
  const {selectedCustomers = []} = meta

  const isSelected = selectedCustomers.some((c) => c.id === customer.id)

  const customerInvitationProgressSelector = () => {
    if (customer.firstPaymentOnMarmalade) {
      return t('modal.steps.selectPayers.paid', {
        days_ago: previouslyNotifiedAt(customer.firstPaymentOnMarmalade),
      })
    }
    if (customer.firstInvoicedOnMarmalade) {
      return t('modal.steps.selectPayers.invoiced', {
        days_ago: previouslyNotifiedAt(customer.firstInvoicedOnMarmalade),
      })
    }
    if (customer.previouslySelectedAt) {
      return t('modal.steps.selectPayers.notified', {
        days_ago: previouslyNotifiedAt(customer.previouslySelectedAt),
      })
    }

    return undefined
  }

  const customerInvitationProgressBlurb = customerInvitationProgressSelector()

  return (
    <Box pr={1}>
      <Button
        className="fs-unmask"
        type="button"
        size="sm"
        variant="outline"
        colorScheme="gray"
        onClick={(e) => {
          e.preventDefault()
          customer.onToggleSelect(customer.id)
        }}
        borderRadius="full"
        borderColor={isSelected ? 'black' : 'gray'}
        px={4}
        _hover={{}}
      >
        {customer.previouslySelectedAt && !isSelected && (
          <Text textTransform="uppercase">
            {t('modal.steps.selectPayers.buttons.previouslySelected')}
          </Text>
        )}
        {isSelected && (
          <Flex alignItems="center" gap="xs">
            <Text textTransform="uppercase">
              {t('modal.steps.selectPayers.buttons.selected')}
            </Text>
            <CheckIcon boxSize="10px" color="green" />
          </Flex>
        )}
        {!customer.previouslySelectedAt && !isSelected && (
          <Text textTransform="uppercase">
            {t('modal.steps.selectPayers.buttons.select')}
          </Text>
        )}
      </Button>
      {customer.previouslySelectedAt && (
        <Text fontSize="xs" color="gray.600" pt={2}>
          {customerInvitationProgressBlurb}
        </Text>
      )}
    </Box>
  )
}

InviteButtonCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      previouslySelectedAt: PropTypes.string,
      firstInvoicedOnMarmalade: PropTypes.string,
      firstPaymentOnMarmalade: PropTypes.string,
      onToggleSelect: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  table: PropTypes.shape({
    options: PropTypes.shape({
      meta: PropTypes.shape({
        selectedCustomers: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
          }),
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}

export const columns = [
  {
    header: 'Customer',
    accessorKey: 'name',
    cell: CustomerNameCell,
    meta: {
      style: {
        width: '19%',
      },
    },
  },
  {
    header: 'Average Invoice Size $',
    accessorKey: 'averageInvoiceSize',
    cell: invoiceSizeCell,
    meta: {
      style: {
        width: '16%',
      },
    },
  },
  {
    header: 'Customer Rating',
    accessorKey: 'customerRating',
    cell: CustomerRatingCell,
    meta: {
      style: {
        width: '13%',
      },
    },
  },
  {
    header: 'Pricing Band %',
    accessorKey: 'pricingBand',
    cell: PricingBandCell,
    meta: {
      style: {
        width: '12%',
        minWidth: '100px',
      },
    },
  },
  {
    header: SelectAllCheckbox,
    accessorKey: 'invite',
    cell: InviteButtonCell,
    enableSorting: false,
    meta: {
      style: {
        width: '12%',
        minWidth: '80px',
      },
    },
  },
]
